/*
  AW · World slides (sections/aw-world-slides.liquid).
  The slides sit in one horizontal scroller with snap points, so swiping is native on
  phones. This adds: the short-name row and arrows (desktop), the "1 / 5" counter,
  arrow keys, and anchor links (#store, #traveller-hive …) that open the right slide,
  on page load and when the address changes. No autoplay.
*/
if (!customElements.get('aw-world-slides')) {
  customElements.define(
    'aw-world-slides',
    class AwWorldSlides extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.count = this.querySelector('[data-count]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.index = 0;
        this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');

        // The slide that fills most of the scroller is the current one
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));

        this.tabs.forEach((tab, i) =>
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.go(i);
            this.remember(i);
          })
        );
        this.prev?.addEventListener('click', () => this.go(this.index - 1));
        this.next?.addEventListener('click', () => this.go(this.index + 1));
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.go(this.index + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.go(this.index - 1);
          }
        });

        this.onHash = () => this.fromHash(true);
        window.addEventListener('hashchange', this.onHash);
        this.setCurrent(0);
        // Opened with an anchor (e.g. from the Home "Meet them →" link)
        if (document.readyState === 'complete') this.fromHash(false);
        else window.addEventListener('load', () => this.fromHash(false), { once: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('hashchange', this.onHash);
      }

      go(i, instant) {
        const target = Math.max(0, Math.min(this.slides.length - 1, i));
        const first = this.slides[0];
        this.track.scrollTo({
          left: this.slides[target].offsetLeft - first.offsetLeft,
          behavior: instant || this.calm.matches ? 'auto' : 'smooth',
        });
        this.setCurrent(target);
      }

      setCurrent(i) {
        if (i < 0) return;
        this.index = i;
        if (this.count) this.count.textContent = String(i + 1);
        this.tabs.forEach((tab, n) => {
          if (n === i) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        });
        this.slides.forEach((slide, n) => slide.toggleAttribute('data-current', n === i));
        if (this.prev) this.prev.disabled = i === 0;
        if (this.next) this.next.disabled = i === this.slides.length - 1;
      }

      // Keep the address in step, without adding history entries or jumping the page
      remember(i) {
        const id = this.slides[i]?.id;
        if (id && history.replaceState) history.replaceState(null, '', '#' + id);
      }

      fromHash(smooth) {
        const id = decodeURIComponent(location.hash.slice(1));
        if (!id) return;
        const i = this.slides.findIndex((slide) => slide.id === id);
        if (i < 0) return;
        this.go(i, !smooth);
        // Bring the whole section into view, heading and all
        const top = this.getBoundingClientRect().top + window.scrollY - 70;
        window.scrollTo({ top, behavior: smooth && !this.calm.matches ? 'smooth' : 'auto' });
      }
    }
  );
}
